<script setup>
/**
 * THE FAILURE BOUNDARY. THE PLAN, §4 P11 and §6 R-6.
 *
 * This component does almost nothing on purpose. It is the one file in the Waslo tree
 * that lives inside Chatwoot's conversation view, and the conversation view is the
 * product: an inbox that will not paint is a business that cannot answer its customers.
 *
 * WHY IT EXISTS AT ALL. Every other Waslo screen is a route of its own — a throw there
 * blanks a page nobody is mid-task on, and the router is one click from somewhere else.
 * This one is mounted INSIDE `ContactPanel.vue`, as a sibling of Conversation actions,
 * Contact attributes and Macros, so an unhandled error here propagates up through
 * `Draggable` and `ContactPanel` to Chatwoot's app-level handler, and Vue unmounts the
 * whole subtree it could not render. The right pane goes blank — including the four
 * native sections that were working perfectly.
 *
 * That is the ONE protection the iframe gave us for free, and the only thing we lose by
 * leaving it: a crashing iframe was a crashing iframe. So it is bought back explicitly,
 * here, with `onErrorCaptured` returning `false` — which stops the error at this component
 * instead of letting it climb — and one inline line where the section's content was.
 *
 * WHAT IS DELIBERATELY NOT IN THIS FILE:
 *
 *  • Any data fetching, any formatting, any state beyond one boolean. `onErrorCaptured`
 *    catches errors from DESCENDANTS, never from the component that declares it — so
 *    every line of real work has to live one level down, in `ConversationWasloBody.vue`,
 *    or it would be outside the very net it is standing in. That is why the body is a
 *    separate file rather than a section of this one, and it is the whole reason the split
 *    exists. `specs/conversation-panel.spec.js` proves it with a child that throws.
 *
 *  • Any unguarded call. The two it cannot avoid — `useWasloI18n()` (a `useI18n()`, which
 *    throws with no vue-i18n installed) and `useUISettings()` (which reaches into vuex) —
 *    are both wrapped, with a working fallback each. A boundary whose own setup can throw
 *    is not a boundary, and the fallback sentence carries an English default of its own
 *    because this is the path taken when reading the translations is what failed.
 *
 * THE HEADER SURVIVES THE FAILURE, and that is deliberate: an agent mid-conversation sees
 * the section she knows, collapsible, saying in one line that this part could not be shown
 * and that the rest of her inbox is fine. An orphaned sentence with no heading reads as
 * the page having corrupted.
 *
 * OPEN BY DEFAULT, WITHOUT A MIGRATION. `isContactSidebarItemOpen` is
 * `!!uiSettings[key]`, so any new key starts FALSE — a section nobody has ever opened is
 * a section nobody will discover. The stored flag is therefore the NEGATIVE
 * (`is_waslo_panel_collapsed`): absent means not collapsed, means open, and the agent's
 * own choice is still remembered from the first time she makes one.
 *
 * The console line is kept: the section degrades silently ON SCREEN, which is right for
 * the agent, and loudly in the console, which is right for us.
 */
import { computed, onErrorCaptured, ref } from 'vue';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWasloI18n } from 'dashboard/routes/dashboard/wasloai/i18n.js';

import ConversationWasloBody from './ConversationWasloBody.vue';

// The conversation is the only thing this section needs. `inboxId` is what the native
// sections beside it take, and it is deliberately NOT accepted: the read is account- and
// conversation-scoped, and a prop nothing uses is a prop somebody later routes a decision
// through.
defineProps({
  conversationId: { type: [Number, String], required: true },
});

/** See the header — absent means open. */
const COLLAPSED_KEY = 'is_waslo_panel_collapsed';

/**
 * The last-resort strings, in English, for when even the translator is unavailable. They
 * are the only user-visible copy in this tree outside `i18n.js`, and they are here rather
 * than there for the reason above.
 */
const FALLBACK_TITLE = 'Waslo AI';
const FALLBACK_BROKEN =
  'This part could not be shown. Everything else in your inbox is working normally.';

const failed = ref(false);

let translate = null;
try {
  ({ t: translate } = useWasloI18n());
} catch {
  translate = null;
}

const say = (key, fallback) => {
  if (!translate) return fallback;
  try {
    const message = translate(key);
    // vue-i18n hands back the KEY when a message is missing, which on screen is
    // `WASLO.PANEL.BROKEN` in the middle of an inbox.
    return typeof message === 'string' && message !== key ? message : fallback;
  } catch {
    return fallback;
  }
};

const title = computed(() => say('WASLO.PANEL.TITLE', FALLBACK_TITLE));
const brokenMessage = computed(() =>
  say('WASLO.PANEL.BROKEN', FALLBACK_BROKEN)
);

// Fallback state for the case where vuex is not reachable: the section still opens and
// closes, it just does not remember across reloads. A section that will not open at all
// is a worse answer to "your settings could not be read".
const localCollapsed = ref(false);
let settings = null;
try {
  settings = useUISettings();
} catch {
  settings = null;
}

// Both reads go through a guard, not just the constructor call: `useUISettings` returns
// closures over a `computed` that is not evaluated until something asks, so a store
// without the settings getter throws HERE, during this component's own render — outside
// the net, and therefore the one throw that could still blank the pane.
const isOpen = computed(() => {
  if (!settings) return !localCollapsed.value;
  try {
    return !settings.isContactSidebarItemOpen(COLLAPSED_KEY);
  } catch {
    return !localCollapsed.value;
  }
});

const toggle = () => {
  // The local flag moves either way, so the two can never disagree about the section's
  // state if the stored one becomes unreachable between one click and the next.
  localCollapsed.value = !localCollapsed.value;
  if (!settings) return;
  try {
    settings.toggleSidebarUIState(COLLAPSED_KEY);
  } catch {
    // Nothing to do: the flag above already moved, so the section still opens and closes
    // — it just will not be remembered across a reload.
  }
};

/**
 * Returning `false` is the entire contract: it tells Vue the error has been handled and
 * must not propagate to the parent chain or to the app-level handler. Everything above
 * this component — `ContactPanel`, the conversation view, the router — carries on as if
 * nothing happened, because from their point of view nothing did.
 *
 * The body is torn down by the `v-else` below rather than left mounted in a half-rendered
 * state: a component that threw during render has no reliable state to recover from, and
 * re-rendering it is how one bad response becomes an error loop.
 */
onErrorCaptured(error => {
  // eslint-disable-next-line no-console
  console.error('[waslo] conversation panel failed, section degraded', error);
  failed.value = true;
  return false;
});
</script>

<template>
  <div data-waslo-conversation-panel>
    <AccordionItem :title="title" :is-open="isOpen" compact @toggle="toggle">
      <p
        v-if="failed"
        class="px-3 py-3 mb-0 text-xs leading-5 text-n-slate-11"
        data-waslo-panel-fallback
      >
        {{ brokenMessage }}
      </p>
      <ConversationWasloBody v-else :conversation-id="conversationId" />
    </AccordionItem>
  </div>
</template>
